import { Tooltip } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import type { BulkBillFileStatus } from "@btnet/services-btnet/handlers/models/bulkBillFileStatus";

import {
    BdsIconCheckCircle,
    BdsIconCircleNotch,
    BdsIconMinusCircle,
    BdsIconX,
    BdsIconXCircle,
    BdsLayout,
} from "@buildertrend/components";

import { TempFileTypes } from "types/enum";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { uploadMultiMedia } from "utilities/document/fileUpload.utils";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { mapToBTServiceFileUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";

import "./BulkBillUploadTracker.less";

import {
    BulkBillUploadHandler,
    type IBulkBillUploadHandler,
} from "./BulkBillUploadModal.api.handler";

const POLL_INTERVAL_MS = 3000;
const defaultHandler = new BulkBillUploadHandler();

type UploadStatus = "uploading" | "uploaded" | "failed";
type ProcessStatus =
    | "waiting"
    | "pending"
    | "processing"
    | "completed"
    | "completedWithWarning"
    | "failed";

interface ITrackerFile {
    fileName: string;
    groupId: string;
    batchId?: string;
    uploadStatus: UploadStatus;
    processStatus: ProcessStatus;
}

export interface IUploadGroup {
    groupId: string;
    fileNames: string[];
    pendingFiles?: File[];
    batchId?: string;
    builderId: number;
}

export interface IBulkBillUploadTrackerProps {
    groups: IUploadGroup[];
    onDismiss: () => void;
    onBillProcessed: () => void;
    handler?: IBulkBillUploadHandler;
}

function isFileDone(file: ITrackerFile): boolean {
    return (
        file.processStatus === "completed" ||
        file.processStatus === "completedWithWarning" ||
        file.processStatus === "failed" ||
        file.uploadStatus === "failed"
    );
}

function UploadStepIcon({ status }: { status: UploadStatus }) {
    switch (status) {
        case "uploaded":
            return <BdsIconCheckCircle size="sm" className="BulkBillUploadTracker-icon--success" />;
        case "failed":
            return <BdsIconXCircle size="sm" className="BulkBillUploadTracker-icon--error" />;
        case "uploading":
        default:
            return <BdsIconCircleNotch size="sm" className="BulkBillUploadTracker-icon--loading" />;
    }
}

function ProcessStepIcon({ status }: { status: ProcessStatus }) {
    switch (status) {
        case "completed":
            return <BdsIconCheckCircle size="sm" className="BulkBillUploadTracker-icon--success" />;
        case "completedWithWarning":
            return (
                <Tooltip
                    title="Something went wrong processing your bill, please check the draft"
                    zIndex={1001}
                >
                    <BdsIconXCircle size="sm" className="BulkBillUploadTracker-icon--error" />
                </Tooltip>
            );
        case "failed":
            return <BdsIconXCircle size="sm" className="BulkBillUploadTracker-icon--error" />;
        case "waiting":
            return <BdsIconMinusCircle size="sm" className="BulkBillUploadTracker-icon--waiting" />;
        case "pending":
        case "processing":
        default:
            return <BdsIconCircleNotch size="sm" className="BulkBillUploadTracker-icon--loading" />;
    }
}

export function BulkBillUploadTracker({
    groups,
    onDismiss,
    onBillProcessed,
    handler = defaultHandler,
}: IBulkBillUploadTrackerProps) {
    const [fileStatuses, setFileStatuses] = useState<ITrackerFile[]>([]);
    const [batchIds, setBatchIds] = useState<string[]>([]);

    const completedPerBatchRef = useRef<Map<string, number>>(new Map());
    const handlerRef = useRef(handler);
    const onDismissRef = useRef(onDismiss);
    const onBillProcessedRef = useRef(onBillProcessed);
    const processedGroupIds = useRef<Set<string>>(new Set());
    const pollIntervalRef = useRef<number | null>(null);

    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    useEffect(() => {
        onBillProcessedRef.current = onBillProcessed;
    }, [onBillProcessed]);

    const uploadGroup = useCallback(async (group: IUploadGroup) => {
        if (!group.pendingFiles) return;

        try {
            const results = await Promise.allSettled(
                group.pendingFiles.map((file) =>
                    uploadMultiMedia({
                        file,
                        tempFileType: TempFileTypes.Receipt,
                        jobId: null,
                        builderId: group.builderId,
                        uploadProgress: () => {},
                        uploadConfig: { shouldUploadFullResolutionPhoto: true },
                    }),
                ),
            );

            const failedIndices = new Set(
                results.map((r, i) => (r.status === "rejected" ? i : -1)).filter((i) => i >= 0),
            );

            // Mark individual files based on their upload outcome
            setFileStatuses((prev) => {
                let groupFileIndex = 0;
                return prev.map((f) => {
                    if (f.groupId !== group.groupId) return f;
                    const idx = groupFileIndex++;
                    if (failedIndices.has(idx)) {
                        return { ...f, uploadStatus: "failed" as const };
                    }
                    return {
                        ...f,
                        uploadStatus: "uploaded" as const,
                        processStatus: "pending" as const,
                    };
                });
            });

            const succeededUploads = results
                .filter(
                    (
                        r,
                    ): r is PromiseFulfilledResult<Awaited<ReturnType<typeof uploadMultiMedia>>> =>
                        r.status === "fulfilled",
                )
                .map((r) => r.value);

            if (succeededUploads.length > 0) {
                const serviceFiles = succeededUploads.map((f) => mapToBTServiceFileUpload(f, true));
                const response = await handlerRef.current.bulkOcrUpload(serviceFiles);
                const newBatchId = response.batchId ?? undefined;

                // Stamp batchId on successfully uploaded files
                setFileStatuses((prev) =>
                    prev.map((f) =>
                        f.groupId === group.groupId && f.uploadStatus === "uploaded"
                            ? { ...f, batchId: newBatchId }
                            : f,
                    ),
                );

                if (newBatchId) {
                    setBatchIds((prev) =>
                        prev.includes(newBatchId) ? prev : [...prev, newBatchId],
                    );
                }
            }
        } catch (e) {
            showAPIErrorMessage(e);
            setFileStatuses((prev) =>
                prev.map((f) =>
                    f.groupId === group.groupId && !isFileDone(f)
                        ? { ...f, uploadStatus: "failed" as const }
                        : f,
                ),
            );
        }
    }, []);

    // Detect new groups and kick off their lifecycle
    useEffect(() => {
        for (const group of groups) {
            if (processedGroupIds.current.has(group.groupId)) continue;
            processedGroupIds.current.add(group.groupId);

            const isPreUploaded = !group.pendingFiles;
            const newFiles: ITrackerFile[] = group.fileNames.map((name) => ({
                fileName: name,
                groupId: group.groupId,
                batchId: group.batchId,
                uploadStatus: isPreUploaded ? ("uploaded" as const) : ("uploading" as const),
                processStatus: isPreUploaded ? ("pending" as const) : ("waiting" as const),
            }));

            setFileStatuses((prev) => [...prev, ...newFiles]);

            if (group.batchId) {
                setBatchIds((prev) =>
                    prev.includes(group.batchId!) ? prev : [...prev, group.batchId!],
                );
            } else if (group.pendingFiles) {
                void uploadGroup(group);
            }
        }
    }, [groups, uploadGroup]);

    // Polling: poll all active batchIds
    const poll = useCallback(async () => {
        if (batchIds.length === 0) return;

        for (const batchId of batchIds) {
            try {
                const response = await handlerRef.current.getProcessingStatus(batchId);
                const apiFiles: BulkBillFileStatus[] = response.files ?? [];

                setFileStatuses((prev) =>
                    prev.map((tracked) => {
                        // Only update files belonging to this batch
                        if (tracked.batchId !== batchId) return tracked;
                        // Never regress a terminal state
                        if (
                            tracked.processStatus === "completed" ||
                            tracked.processStatus === "completedWithWarning" ||
                            tracked.processStatus === "failed"
                        ) {
                            return tracked;
                        }

                        const match = apiFiles.find(
                            (f: BulkBillFileStatus) => f.fileName === tracked.fileName,
                        );
                        if (!match) return tracked;

                        let processStatus: ProcessStatus;
                        switch (match.status) {
                            case "Completed":
                                processStatus = "completed";
                                break;
                            case "CompletedWithWarning":
                                processStatus = "completedWithWarning";
                                break;
                            case "Failed":
                                processStatus = "failed";
                                break;
                            case "Processing":
                                processStatus = "processing";
                                break;
                            default:
                                processStatus = "pending";
                        }
                        return { ...tracked, processStatus };
                    }),
                );

                const newCompletedCount = apiFiles.filter(
                    (f: BulkBillFileStatus) =>
                        f.status === "Completed" || f.status === "CompletedWithWarning",
                ).length;
                const prevCount = completedPerBatchRef.current.get(batchId) ?? 0;
                if (newCompletedCount > prevCount) {
                    completedPerBatchRef.current.set(batchId, newCompletedCount);
                    onBillProcessedRef.current();
                }
            } catch (e) {
                showAPIErrorMessage(e);
            }
        }
    }, [batchIds]);

    // Start/restart polling when batchIds change
    useEffect(() => {
        if (pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        if (batchIds.length === 0) return;

        void poll();
        pollIntervalRef.current = window.setInterval(() => {
            void poll();
        }, POLL_INTERVAL_MS);

        return () => {
            if (pollIntervalRef.current !== null) {
                window.clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [batchIds, poll]);

    // Stop polling when all files are done
    const allDone = fileStatuses.length > 0 && fileStatuses.every(isFileDone);
    useEffect(() => {
        if (allDone && pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [allDone]);

    // Counts
    const totalCount = fileStatuses.length;
    const uploadingCount = fileStatuses.filter((f) => f.uploadStatus === "uploading").length;
    const processingCount = fileStatuses.filter(
        (f) => f.processStatus === "pending" || f.processStatus === "processing",
    ).length;
    const doneCount = fileStatuses.filter((f) => f.processStatus === "completed").length;
    const warningCount = fileStatuses.filter(
        (f) => f.processStatus === "completedWithWarning",
    ).length;
    const failedCount = fileStatuses.filter(
        (f) => f.processStatus === "failed" || f.uploadStatus === "failed",
    ).length;

    let headerText: string;
    if (allDone && failedCount === 0 && warningCount === 0) {
        headerText = `${totalCount} bill${totalCount === 1 ? "" : "s"} processed`;
    } else if (allDone) {
        const parts: string[] = [];
        if (doneCount > 0) parts.push(`${doneCount} processed`);
        if (warningCount > 0)
            parts.push(`${warningCount} need${warningCount === 1 ? "s" : ""} review`);
        if (failedCount > 0) parts.push(`${failedCount} failed`);
        headerText = parts.join(", ");
    } else {
        const parts: string[] = [];
        if (uploadingCount > 0) parts.push(`${uploadingCount} uploading`);
        if (processingCount > 0) parts.push(`${processingCount} processing`);
        if (doneCount > 0) parts.push(`${doneCount} done`);
        headerText = parts.join(" · ") || "Preparing...";
    }

    // Group files for display
    const groupIds = [...new Set(fileStatuses.map((f) => f.groupId))];

    return (
        <div className="BulkBillUploadTracker" data-testid="bulkBillUploadTracker">
            <BdsLayout displayType="HCF">
                <div className="flex flex-row BulkBillUploadTracker-header">
                    <div className="flex flex-col justify-center grow">
                        <BTText text={headerText} size="distinct-sm" />
                    </div>
                    <div className="flex flex-col justify-center">
                        <BTPopover content="Close">
                            <BTButton
                                data-testid="bulkTrackerDismiss"
                                type="tertiary"
                                icon={<BdsIconX />}
                                aria-label="Close"
                                onClick={() => onDismissRef.current()}
                            />
                        </BTPopover>
                    </div>
                </div>
                <div className="BulkBillUploadTracker-list">
                    {groupIds.map((groupId, groupIndex) => {
                        const groupFiles = fileStatuses.filter((f) => f.groupId === groupId);
                        return (
                            <div key={groupId}>
                                {groupIndex > 0 && (
                                    <div className="BulkBillUploadTracker-groupDivider" />
                                )}
                                {groupFiles.map((item, index) => (
                                    <div
                                        key={`${item.groupId}-${item.fileName}-${index}`}
                                        className="flex flex-row BulkBillUploadTracker-listItem"
                                    >
                                        <div className="flex flex-col grow BulkBillUploadTracker-fileName">
                                            <BTText text={item.fileName ?? ""} size="normal-md" />
                                        </div>
                                        <div className="flex flex-col justify-center BulkBillUploadTracker-steps">
                                            <div className="BulkBillUploadTracker-stepPair">
                                                <UploadStepIcon status={item.uploadStatus} />
                                                <span className="BulkBillUploadTracker-stepArrow">
                                                    ›
                                                </span>
                                                <ProcessStepIcon status={item.processStatus} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
                {allDone && failedCount > 0 && (
                    <div className="BulkBillUploadTracker-footer">
                        <BTText
                            text={`${failedCount} file${
                                failedCount === 1 ? "" : "s"
                            } could not be processed.`}
                            size="normal-md"
                            className="BulkBillUploadTracker-errorText"
                        />
                    </div>
                )}
            </BdsLayout>
        </div>
    );
}
